/**
 * Hooks LeadBeton Sprint 5 (Modules 11 Fournisseurs/Achats · 12 Maintenance moules).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  operationsApi,
  type BcaListParams,
  type CreateBcaPayload,
  type CreateFournisseurPayload,
  type CreateMaintenancePayload,
  type CreateReceptionPayload,
  type FournisseurListParams,
  type MaintenanceListParams,
  type UpdateBcaPayload,
  type UpdateFournisseurPayload,
  type UpdateMaintenancePayload,
} from '@/api/operations';

const FOURNISSEURS_KEY = ['operations', 'beton', 'fournisseurs']        as const;
const BCA_KEY          = ['operations', 'beton', 'bca']                  as const;
const RECEPTIONS_KEY   = ['operations', 'beton', 'receptions']           as const;
const MAINTENANCE_KEY  = ['operations', 'beton', 'maintenances-moule']   as const;
const MAINT_ALERT_KEY  = ['operations', 'beton', 'maintenances-moule', 'alertes'] as const;

// ── Fournisseurs ─────────────────────────────────────────────────────────

export const useFournisseurs = (params: FournisseurListParams = {}) =>
  useQuery({
    queryKey:  [...FOURNISSEURS_KEY, params],
    queryFn:   () => operationsApi.beton.listFournisseurs(params),
    staleTime: 60_000,
  });

export const useFournisseur = (id: string | undefined) =>
  useQuery({
    queryKey:  [...FOURNISSEURS_KEY, id],
    queryFn:   () => operationsApi.beton.getFournisseur(id!),
    enabled:   !!id,
    staleTime: 30_000,
  });

export const useCreateFournisseur = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateFournisseurPayload) => operationsApi.beton.createFournisseur(payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: FOURNISSEURS_KEY }),
  });
};

export const useUpdateFournisseur = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateFournisseurPayload }) =>
      operationsApi.beton.updateFournisseur(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: FOURNISSEURS_KEY }),
  });
};

export const useDeleteFournisseur = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.beton.deleteFournisseur(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: FOURNISSEURS_KEY }),
  });
};

// ── BCA + Réceptions ─────────────────────────────────────────────────────

export const useBca = (params: BcaListParams = {}) =>
  useQuery({
    queryKey:  [...BCA_KEY, params],
    queryFn:   () => operationsApi.beton.listBca(params),
    staleTime: 30_000,
  });

export const useBcaDetail = (id: string | undefined) =>
  useQuery({
    queryKey:  [...BCA_KEY, id],
    queryFn:   () => operationsApi.beton.getBca(id!),
    enabled:   !!id,
    staleTime: 15_000,
  });

const invalidateBca = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: BCA_KEY });
  qc.invalidateQueries({ queryKey: RECEPTIONS_KEY });
};

export const useCreateBca = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateBcaPayload) => operationsApi.beton.createBca(payload),
    onSuccess:  () => invalidateBca(qc),
  });
};

export const useUpdateBca = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateBcaPayload }) =>
      operationsApi.beton.updateBca(id, payload),
    onSuccess: () => invalidateBca(qc),
  });
};

export const useEmettreBca = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.beton.emettreBca(id),
    onSuccess:  () => invalidateBca(qc),
  });
};

export const useAnnulerBca = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.beton.annulerBca(id),
    onSuccess:  () => invalidateBca(qc),
  });
};

export const useDeleteBca = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.beton.deleteBca(id),
    onSuccess:  () => invalidateBca(qc),
  });
};

export const useCreateReception = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bcaId, payload }: { bcaId: string; payload: CreateReceptionPayload }) =>
      operationsApi.beton.createReception(bcaId, payload),
    onSuccess: () => {
      invalidateBca(qc);
      qc.invalidateQueries({ queryKey: ['operations', 'beton', 'matieres'] });
      qc.invalidateQueries({ queryKey: ['operations', 'beton', 'stocks'] });
    },
  });
};

// ── Maintenance moules ───────────────────────────────────────────────────

export const useMaintenances = (params: MaintenanceListParams = {}) =>
  useQuery({
    queryKey:  [...MAINTENANCE_KEY, params],
    queryFn:   () => operationsApi.beton.listMaintenances(params),
    staleTime: 30_000,
  });

export const useMaintenanceAlertes = () =>
  useQuery({
    queryKey:        MAINT_ALERT_KEY,
    queryFn:         () => operationsApi.beton.getMaintenanceAlertes(),
    staleTime:       60_000,
    refetchInterval: 300_000,
  });

const invalidateMaintenance = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: MAINTENANCE_KEY });
  qc.invalidateQueries({ queryKey: ['operations', 'beton', 'moules'] });
};

export const useCreateMaintenance = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateMaintenancePayload) => operationsApi.beton.createMaintenance(payload),
    onSuccess:  () => invalidateMaintenance(qc),
  });
};

export const useUpdateMaintenance = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateMaintenancePayload }) =>
      operationsApi.beton.updateMaintenance(id, payload),
    onSuccess: () => invalidateMaintenance(qc),
  });
};

export const useDemarrerMaintenance = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.beton.demarrerMaintenance(id),
    onSuccess:  () => invalidateMaintenance(qc),
  });
};

export const useTerminerMaintenance = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload?: { resultat?: string; coutCents?: number } }) =>
      operationsApi.beton.terminerMaintenance(id, payload ?? {}),
    onSuccess: () => invalidateMaintenance(qc),
  });
};

export const useDeleteMaintenance = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => operationsApi.beton.deleteMaintenance(id),
    onSuccess:  () => invalidateMaintenance(qc),
  });
};
